/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useDashboard } from '@/hooks'
import type { SavedView, SavedViewPage } from '@/types/dashboard-types'

/**
 * One page's saved views, from the profile's savedViews. `V` is the page's own view shape; the
 * profile stores it as given and hands it back as given.
 */
export const useSavedViews = <V extends SavedView>(page: SavedViewPage) => {
  const { configuration, saveView, deleteView } = useDashboard()
  const byName = (configuration.savedViews?.[page] ?? {}) as Record<string, V>
  const views = Object.values(byName)

  /** Stores `view`, and when it opens by default, takes that from whichever view had it. */
  const store = (view: V) => {
    if (view.isDefault) {
      views
        .filter((other) => other.isDefault && other.name !== view.name)
        .forEach((other) => saveView(page, { ...other, isDefault: false }))
    }
    saveView(page, view)
  }

  /** Validates `name` and stores `build(trimmedName)` under it. Returns an error message, or null once saved. */
  const storeNew = (name: string, build: (trimmedName: string) => V): string | null => {
    const trimmedName = name.trim()
    if (!trimmedName) return 'Please enter a name'
    if (byName[trimmedName]) return 'A saved view with this name already exists'
    store(build(trimmedName))
    return null
  }

  return {
    views,
    byName,
    viewNames: Object.keys(byName),
    defaultView: views.find((view) => view.isDefault) ?? null,
    store,
    storeNew,
    remove: (name: string) => deleteView(page, name),
  }
}
